import { useEffect, useState } from "react";
import { askForReload, isOriginReachable, untilReloadAllowed } from "./reloadSteps";

/**
 * Gets the page reloaded for a part of it that cannot recover in place, in the steps of the chunk
 * recovery: asks the origin whether it answers, asks for the recovery reload when it does, and in
 * every case tries again when the rate limit of the recovery reload lets go, until a reload takes the
 * page. A reload that is held back, an origin out of reach, a webview that keeps the page and storage
 * that throws all leave the next attempt armed, so the kiosk is never left waiting for good.
 *
 * The first attempt runs at once. An attempt that is not allowed waits, also one whose probe is on its
 * way, and runs once it is. At most one timer is pending: the timeout of the probe, or the wait for the
 * next attempt; an attempt that is disposed of gives its probe up and leaves none.
 *
 * @param reason what the reload recovers from, for its log
 * @param allowed whether an attempt may run now
 */
export const useRecoveryReload = (reason: string, allowed: boolean): void => {
	// Whether an attempt is due: the first one at once, each next one when the rate limit lets go.
	const [due, setDue] = useState(true);

	useEffect(() => {
		if (!(due && allowed)) {
			return;
		}
		let disposed = false;
		// Given up with the attempt, so that neither the fetch of the probe nor its timeout outlives it.
		const probe = new AbortController();
		const answered = (reachable: boolean) => {
			if (disposed) {
				return;
			}
			if (reachable) {
				askForReload(reason);
			}
			setDue(false);
		};
		// A microtask later: StrictMode runs this effect twice on mount and disposes the first run
		// before then, which must send nothing.
		queueMicrotask(() => {
			if (!disposed) {
				// A probe that cannot even be sent counts as an origin that does not answer.
				isOriginReachable(probe.signal).then(answered, () => answered(false));
			}
		});
		return () => {
			disposed = true;
			probe.abort();
		};
	}, [due, allowed, reason]);

	useEffect(() => {
		if (due) {
			return;
		}
		const timer = window.setTimeout(() => setDue(true), untilReloadAllowed());
		return () => window.clearTimeout(timer);
	}, [due]);
};
